import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Badge,
  Card,
} from 'react-bootstrap';

export type ReadOnlyScoreFeedType =
  | 'judge-complete'
  | 'contestant-aoc-complete'
  | 'age-group-aoc-complete'
  | 'division-aoc-complete'
  | 'test-score'
  | 'side-awards-complete';

export interface ReadOnlyScoreFeedItem {
  id: string;

  type: ReadOnlyScoreFeedType;

  judgeId?: number;

  judgeLabel?: string;

  registeredPageantContestantId: number;

  contestantNumber?: number | string;

  contestantName: string;

  pageantAvailableEventId: number;

  aocName: string;

  ageGroupId?: number;

  ageGroupName?: string;

  divisionId?: number;

  divisionName?: string;

  completedAt: string;
}

type ReadOnlyLiveScoreFeedProps = {
  notifications: ReadOnlyScoreFeedItem[];

  maxVisible?: number;
};

const formatTimeAgo = (
  value: string,
): string => {
  const date =
    new Date(value);

  const diffSeconds =
    Math.max(
      0,
      Math.floor(
        (Date.now() -
          date.getTime()) /
          1000,
      ),
    );

  if (diffSeconds < 5) {
    return 'Just now';
  }

  if (diffSeconds < 60) {
    return `${diffSeconds}s ago`;
  }

  const diffMinutes =
    Math.floor(
      diffSeconds / 60,
    );

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours =
    Math.floor(
      diffMinutes / 60,
    );

  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  return date.toLocaleDateString();
};

const ReadOnlyLiveScoreFeed: React.FC<
  ReadOnlyLiveScoreFeedProps
> = ({
  notifications,
  maxVisible = 30,
}) => {
  const [, forceTimeUpdate] =
    useState(0);

  // Refresh the "2m ago" text without
  // needing another SignalR event.
  useEffect(() => {
    const interval =
      window.setInterval(
        () => {
          forceTimeUpdate(
            (value) =>
              value + 1,
          );
        },
        30000,
      );

    return () => {
      window.clearInterval(
        interval,
      );
    };
  }, []);

  const visibleNotifications =
    useMemo(
      () =>
        notifications
          .slice()
          .sort(
            (a, b) =>
              new Date(
                b.completedAt,
              ).getTime() -
              new Date(
                a.completedAt,
              ).getTime(),
          )
          .slice(
            0,
            maxVisible,
          ),
      [
        notifications,
        maxVisible,
      ],
    );

  return (
    <Card className="shadow-sm border-0 mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <div className="fw-bold">
            Live Judging Feed
          </div>

          <div className="text-muted small">
            Updates as contestants
            are scored
          </div>
        </div>

        <Badge className="text-large" bg="primary">
          {
            visibleNotifications.length
          }
        </Badge>
      </Card.Header>

      <Card.Body
        className="p-2"
        style={{
          maxHeight: 500,
          overflowY: 'auto',
        }}
      >
      {visibleNotifications.map(
  (notification) => {
    const isTestScore =
      notification.type ===
      'test-score';

    const isContestantComplete =
      notification.type ===
      'contestant-aoc-complete';

    const isAgeGroupComplete =
      notification.type ===
      'age-group-aoc-complete';

    const isDivisionComplete =
      notification.type ===
      'division-aoc-complete';

      const isSideAwardsComplete =
  notification.type ===
  'side-awards-complete';

    const isCompletionNotification =
      isContestantComplete ||
      isAgeGroupComplete ||
      isDivisionComplete ||
      isSideAwardsComplete;

    return (
      <div
        key={notification.id}
        className={`alert ${
  isTestScore
    ? 'alert-danger'
    : isSideAwardsComplete
      ? 'alert-light'
      : isCompletionNotification
        ? 'alert-success'
        : 'alert-info'
} mb-0 py-2 px-3`}
        role="alert"
      >
       {isTestScore ? (
  <>
    <div className="fw-bold">
      🚨 TEST RECEIVED for{' '}
      {notification.judgeLabel ??
        'Judge'}
    </div>
  </>
) : isSideAwardsComplete ? (
  <>
    <div className="small">
       ✅   <strong>
        SIDE AWARDS COMPLETE
      </strong>
       {' · '}
      <strong>
        {notification.divisionName ??
          'Division'}
      </strong>

      {notification.ageGroupName && (
        <>
          {' · '}
          <strong>
            {notification.ageGroupName}
          </strong>
        </>
      )}

     

    
    </div>
  </>
) : isContestantComplete ? (
        <>
  <div className="small">
    ✅{' '}
    <strong>
      #{notification.contestantNumber ?? '—'}{' '}
      {notification.contestantName}
    </strong>
    {' — '}
    <strong className="text-uppercase">
      COMPLETE
    </strong>
    {' — '}
    {notification.aocName}
  </div>
</>
        ) : isAgeGroupComplete ? (
          <>
            <div className="fw-bold">
              ✅ Age Group Complete
            </div>

            <div className="small">
              <strong>
                {notification.ageGroupName ??
                  'Age Group'}
              </strong>{' '}
              completed{' '}
              <strong>
                {notification.aocName}
              </strong>
            </div>
          </>
        ) : isDivisionComplete ? (
          <>
            <div className="fw-bold">
              ✅ Division Complete
            </div>

            <div className="small">
              <strong>
                {notification.divisionName ??
                  'Division'}
              </strong>{' '}
              completed{' '}
              <strong>
                {notification.aocName}
              </strong>
            </div>
          </>
        ) : (
          <>
            <div className="fw-bold small">
              {notification.judgeLabel ??
                'Judge'}{' '}
              submitted scores
            </div>

            <div className="small">
              <strong>
                #
                {notification.contestantNumber ??
                  '—'}{' '}
                {notification.contestantName}
              </strong>{' '}
              ·{' '}
              <strong>
                {notification.aocName}
              </strong>
            </div>
          </>
        )}

        <div
          className="text-muted mt-1"
          style={{
            fontSize: '0.72rem',
          }}
        >
          {formatTimeAgo(
            notification.completedAt,
          )}
        </div>
      </div>
    );
  },
)}
      </Card.Body>
    </Card>
  );
};

export default React.memo(
  ReadOnlyLiveScoreFeed,
);